/**
 * API Client
 *
 * Axios-based HTTP client with interceptors for:
 * - Authentication
 * - Error handling
 * - Request/response logging
 * - Correlation IDs
 */

import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig, AxiosResponse } from 'axios';
import { apiConfig, isRetryableStatus, calculateRetryDelay } from '@/config/api.config';
import { getAccessToken } from '@/lib/auth/tokenManager';
import type { ApiError } from '@/types';

/**
 * Create API client instance
 */
const apiClient: AxiosInstance = axios.create(apiConfig);

/**
 * Request retry tracking
 */
const retryTracker = new WeakMap<InternalAxiosRequestConfig, number>();

/**
 * Request interceptor
 * Adds correlation ID and auth tokens
 */
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    // Add correlation ID for request tracking
    const correlationId = crypto.randomUUID();
    config.headers['X-Correlation-ID'] = correlationId;

    // Add auth token if available
    try {
      const token = await getAccessToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (error) {
      // Silently fail if auth is not configured or token retrieval fails
      console.debug('[API] No auth token available', error);
    }

    // Log request in development
    if (import.meta.env.DEV) {
      console.log(`[API Request] ${config.method?.toUpperCase()} ${config.url}`, {
        correlationId,
        params: config.params,
        data: config.data,
        hasAuth: !!config.headers.Authorization,
      });
    }

    return config;
  },
  (error) => {
    console.error('[API Request Error]', error);
    return Promise.reject(error);
  }
);

/**
 * Response interceptor
 * Handles errors and retries
 */
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    // Log response in development
    if (import.meta.env.DEV) {
      const correlationId = response.config.headers['X-Correlation-ID'];
      console.log(
        `[API Response] ${response.config.method?.toUpperCase()} ${response.config.url}`,
        {
          correlationId,
          status: response.status,
          data: response.data,
        }
      );
    }

    return response;
  },
  async (error: AxiosError) => {
    const config = error.config as InternalAxiosRequestConfig;

    if (!config) {
      return Promise.reject(error);
    }

    const correlationId = config.headers?.['X-Correlation-ID'];
    const status = error.response?.status;

    // Log error in development
    if (import.meta.env.DEV) {
      console.error(`[API Error] ${config.method?.toUpperCase()} ${config.url}`, {
        correlationId,
        status,
        message: error.message,
        data: error.response?.data,
      });
    }

    // Handle retry logic for retryable errors
    if (status && isRetryableStatus(status)) {
      const retryCount = retryTracker.get(config) || 0;
      const maxRetries = 3;

      if (retryCount < maxRetries) {
        retryTracker.set(config, retryCount + 1);

        // Calculate delay with exponential backoff
        const delay = calculateRetryDelay(retryCount + 1);

        console.log(
          `[API Retry] Attempt ${retryCount + 1}/${maxRetries} after ${delay}ms for ${config.url}`
        );

        // Wait before retry
        await new Promise((resolve) => setTimeout(resolve, delay));

        // Retry the request
        return apiClient(config);
      }
    }

    // --- 401 token-refresh retry (single attempt) ---
    // If we got a 401 it may be because the access token expired mid-session.
    // Try to obtain a fresh token and replay the request exactly once.
    if (status === 401 && !(config as any).__auth_retry) {
      try {
        const freshToken = await getAccessToken();
        if (freshToken) {
          (config as any).__auth_retry = true;
          config.headers.Authorization = `Bearer ${freshToken}`;
          console.log(`[API Retry-Auth] Retrying ${config.url} with refreshed token`);
          return apiClient(config);
        }
      } catch {
        // fall through to normal error handling
      }
    }

    // Transform error to ApiError
    const apiError: ApiError = {
      message: error.message || 'An unexpected error occurred',
      code: (error.response?.data as any)?.code || 'UNKNOWN_ERROR',
      statusCode: status,
      details: error.response?.data as Record<string, unknown> | undefined,
      timestamp: '',
      name: '',
    };

    return Promise.reject(apiError);
  }
);

/**
 * Get the API client instance
 */
export function getApiClient(): AxiosInstance {
  return apiClient;
}

/**
 * Helper to build full API URL
 */
export function buildApiUrl(path: string, params?: Record<string, string | number>): string {
  let url = `${apiConfig.baseURL}${path}`;

  // Replace path parameters
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      url = url.replace(`:${key}`, String(value));
    });
  }

  return url;
}

export default apiClient;
